import type { ReactNode } from 'react'

interface DetailShellProps {
  /** Pass a rendered <Breadcrumbs /> — composition keeps this shell type-simple. */
  breadcrumbs?: ReactNode
  title: ReactNode
  /** Status/severity pills beside the title. */
  badges?: ReactNode
  /** Secondary identifier under the title — run id, report id. Mono face. */
  eyebrow?: ReactNode
  /** Right-aligned buttons and menus. */
  actions?: ReactNode
  /** Pass a rendered <TabStrip />. */
  tabs?: ReactNode
  children?: ReactNode
}

/**
 * The shared frame for every detail surface: ETL Explorer, Pipeline View and
 * Report Detail all sit in this.
 *
 * Built in W1 even though those pages arrive in phases 3–6, so the design is
 * proven once rather than diverging three ways later. It is also why detail
 * surfaces can be routes today and a persistent rail later with no rework.
 */
export function DetailShell({
  breadcrumbs,
  title,
  badges,
  eyebrow,
  actions,
  tabs,
  children,
}: DetailShellProps) {
  return (
    <section className="lh-card">
      <header className="lh-detail-header">
        {breadcrumbs}
        <div className="lh-detail-titlerow">
          <div className="lh-detail-titlegroup">
            <h1 className="lh-display-title">{title}</h1>
            {badges}
          </div>
          {actions && <div className="lh-detail-actions">{actions}</div>}
        </div>
        {eyebrow && <span className="lh-detail-eyebrow">{eyebrow}</span>}
      </header>
      {tabs}
      <div className="lh-card-body">{children}</div>
    </section>
  )
}
